import { ArrowUpRight, ShieldCheck, Landmark, MapPinned } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { ProductCta, PageHero, Reveal } from "../components/ProductPageShared";
import { usePageMeta } from "../hooks/usePageMeta";

const features = [
  {
    icon: ShieldCheck,
    title: "OIC-compliant",
    copy: "Built to Washington State regulatory standards — a title plant you can actually insure from.",
  },
  {
    icon: Landmark,
    title: "Back to State Patent",
    copy: "The full chain, not a shortcut — title searches in minutes instead of days.",
  },
  {
    icon: MapPinned,
    title: "County by county",
    copy: "Rolling out across all 39 Washington counties. As new counties go live, they're yours if they fall within your subscription plan.",
  },
];

const pricing = [
  { n: "01", title: "One Low Monthly Lease", copy: "A single, straightforward monthly lease gets you access to ART." },
  { n: "02", title: "Pay at Successful Close", copy: "Beyond the lease, you only pay when a transaction closes. If the deal doesn't close, neither do we." },
  { n: "03", title: "Every County, Forever", copy: "As new counties go live, they're yours if they fall within your subscription plan. Your coverage grows with ART, with a simple points-based subscription." },
];

export default function ArtPage() {
  usePageMeta({
    title: "ART — AI Rendered Title, an AI-Powered Title Plant",
    description: "ART is an OIC-compliant, AI-powered title plant back to State Patent — title searches in minutes instead of days, rolling out county by county across Washington State.",
    path: "/art",
  });
  return (
    <main className="bg-navy min-h-screen" data-testid="art-page">
      <Navbar />
      <div className="relative">
        <img
          src="/assets/products/marks/art-mark.png"
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute top-36 right-16 w-[22rem] opacity-[0.5] pointer-events-none select-none"
        />
        <PageHero
          kicker="Product — ART"
          title="AI Rendered Title"
          accent="An AI-powered title plant."
          sub="ART fundamentally changes how title searches are performed — an OIC-compliant, AI-powered title plant back to State Patent, purpose-built for the modern title professional."
        >
          <div className="flex flex-wrap gap-4 mt-9">
            <a
              href="https://art.titlecat.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="art-launch-btn"
              className="group relative px-7 py-4 bg-tech text-fog font-mono-accent text-[11px] tracking-[0.22em] uppercase overflow-hidden"
            >
              <span className="absolute inset-0 bg-tech-bright translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" />
              <span className="relative flex items-center gap-2">
                Launch ART <ArrowUpRight className="h-4 w-4 group-hover:rotate-45 transition-transform duration-300" />
              </span>
            </a>
            <a
              href="https://northwesttitlealliance.com/#roadmap"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="art-roadmap-btn"
              className="px-7 py-4 border border-steel text-fog/80 hover:border-tech-bright hover:text-tech-bright font-mono-accent text-[11px] tracking-[0.22em] uppercase transition-colors duration-300 flex items-center gap-2"
            >
              County Roadmap <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </PageHero>
      </div>
      <section className="relative bg-navy pb-16 md:pb-20" data-testid="art-features">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Reveal
                key={f.title}
                index={i}
                className="border border-steel/30 bg-navy-deep/60 p-7 md:p-9 hover:border-tech/70 transition-colors duration-500"
              >
                <Icon className="h-6 w-6 text-tech-bright" strokeWidth={1.5} />
                <h2 className="font-display font-semibold tracking-tight text-lg md:text-xl text-fog mt-5">{f.title}</h2>
                <p className="text-[13.5px] text-fog/60 leading-relaxed mt-3">{f.copy}</p>
              </Reveal>
            );
          })}
        </div>
      </section>
      <section className="relative bg-navy pb-20 md:pb-24" data-testid="art-pricing">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="font-mono-accent text-[11px] tracking-[0.35em] uppercase text-tech-bright mb-8">Pricing philosophy</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {pricing.map((p, i) => (
              <Reveal key={p.n} index={i} className="relative border border-steel/30 bg-navy-deep/60 p-7 md:p-9">
                <span className="absolute top-7 right-7 font-mono-accent text-[11px] text-fog/30">{p.n}</span>
                <h3 className="font-display font-semibold tracking-tight text-lg text-fog max-w-[16ch]">{p.title}</h3>
                <p className="text-[13.5px] text-fog/60 leading-relaxed mt-3">{p.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <ProductCta heading="Ready to search title in minutes?" />
      <Footer />
    </main>
  );
}
